// Tracks attachments the user has downloaded, so the malware warning toast
// can be suppressed on subsequent previews of the same attachment.
// Keyed by Matrix event ID and persisted across sessions via localStorage.
// The set is cleared at logout so suppression does not carry across users on
// shared devices (see matrixAuthService keysToRemove).

const STORAGE_KEY = 'tim:downloaded-attachments';

/** Maximum entries before the oldest is evicted (FIFO). Prevents unbounded localStorage growth. */
export const MAX_ENTRIES = 1000;

// Module-level write-through cache. `null` means "not yet loaded from storage".
let _cached = null;

const loadSet = () => {
  if (_cached !== null) return _cached;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    _cached = raw ? new Set(JSON.parse(raw)) : new Set();
  } catch {
    _cached = new Set();
  }
  return _cached;
};

const saveSet = (set) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...set]));
    // Only update the in-memory cache after a successful write so that a
    // write failure (e.g. QuotaExceededError) does not promote the new entry
    // into the cache — the entry will be absent again after the next reload.
    _cached = set;
  } catch {
    // localStorage may be unavailable or full — failing silently is acceptable;
    // the only consequence is showing the warning toast again after a page reload.
  }
};

// Invalidate the in-memory cache when another tab writes to the same key so
// cross-tab reads stay consistent.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === STORAGE_KEY) _cached = null;
  });
}

export const markAttachmentDownloaded = (eventId) => {
  if (!eventId) return;
  const current = loadSet();
  if (current.has(eventId)) return;
  // Copy-on-write: never mutate the cached set so a failed write leaves the
  // cache in its pre-call state.
  const next = new Set(current);
  // FIFO eviction: remove the oldest entry when the cap is reached.
  if (next.size >= MAX_ENTRIES) {
    next.delete(next.values().next().value);
  }
  next.add(eventId);
  saveSet(next);
};

export const wasAttachmentDownloaded = (eventId) => {
  if (!eventId) return false;
  return loadSet().has(eventId);
};

/** Exposed for unit testing only — resets the in-memory cache. */
export const _resetForTesting = () => { _cached = null; };
